import type { Style } from "@bezpart-board/shared";

export type StyleCarrier = {
  style?: Style;
  stroke?: string;
  strokeWidth?: number;
  strokeOpacity?: number;
  strokeLinecap?: Style["strokeLinecap"];
  strokeLinejoin?: Style["strokeLinejoin"];
  strokeDash?: Style["strokeDash"];
  fill?: string;
  color?: string;
};

/**
 * Объекты сцены объявлены с индексной сигнатурой, а StyleCarrier состоит
 * только из необязательных полей — TypeScript в таком случае требует хотя бы
 * одно совпадающее свойство и отвергал SceneObject. Из-за этого проверка типов
 * выдавала десятки ложных ошибок и настоящие в них тонули.
 */
export type StyleSource = StyleCarrier & Record<string, unknown>;

/** Читает style v2 или legacy v1 flat fields. */
export function readObjectStyle(obj: StyleSource): Style {
  const s = obj.style ?? {};
  return {
    stroke: s.stroke ?? obj.stroke,
    strokeWidth: s.strokeWidth ?? obj.strokeWidth,
    strokeOpacity: s.strokeOpacity ?? obj.strokeOpacity,
    strokeLinecap: s.strokeLinecap ?? obj.strokeLinecap,
    strokeLinejoin: s.strokeLinejoin ?? obj.strokeLinejoin,
    strokeDash: s.strokeDash ?? obj.strokeDash,
    fill: s.fill ?? obj.fill,
    fillOpacity: s.fillOpacity,
    color: s.color,
    backgroundColor: s.backgroundColor ?? obj.color,
    fontFamily: s.fontFamily,
    fontSize: s.fontSize,
    fontWeight: s.fontWeight,
    fontStyle: s.fontStyle,
    textDecoration: s.textDecoration,
    textAlign: s.textAlign,
    borderColor: s.borderColor,
    borderWidth: s.borderWidth,
  };
}

export function stickyBackground(obj: StyleSource): string | undefined {
  const s = readObjectStyle(obj);
  return s.backgroundColor;
}

export function pathStrokeWidth(obj: StyleSource): number {
  const w = readObjectStyle(obj).strokeWidth;
  return typeof w === "number" && Number.isFinite(w) ? w : 3;
}

export function pathStrokeColor(obj: StyleSource): string {
  return readObjectStyle(obj).stroke ?? "#1a1a2e";
}

export function pathLinecap(obj: StyleSource): NonNullable<Style["strokeLinecap"]> {
  return readObjectStyle(obj).strokeLinecap ?? "round";
}

export function pathLinejoin(obj: StyleSource): NonNullable<Style["strokeLinejoin"]> {
  return readObjectStyle(obj).strokeLinejoin ?? "round";
}

export function pathStrokeDash(obj: StyleSource): Style["strokeDash"] {
  return readObjectStyle(obj).strokeDash ?? "solid";
}

/** SVG stroke-dasharray для solid/dashed/dotted (wavy — только line/arrow). */
export function strokeDasharrayForStyle(
  dash: Style["strokeDash"] | undefined,
  strokeWidth: number
): string | null {
  if (!dash || dash === "solid" || dash === "wavy") return null;
  const w = Math.max(1, strokeWidth);
  if (dash === "dashed") return `${w * 4} ${w * 2.5}`;
  if (dash === "dotted") return `0 ${w * 2}`;
  return null;
}

export function applyStrokeDashAttr(
  el: SVGPathElement | SVGLineElement | SVGPolylineElement,
  dash: Style["strokeDash"] | undefined,
  strokeWidth: number
): void {
  const arr = strokeDasharrayForStyle(dash, strokeWidth);
  if (arr) {
    el.setAttribute("stroke-dasharray", arr);
  } else {
    el.removeAttribute("stroke-dasharray");
  }
}
